import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Languages, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { addMissingTranslations } from '@/utils/addMissingTranslations';

export const MissingTranslationsAdder = () => {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleAddTranslations = async () => {
    setIsAdding(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      await addMissingTranslations();
      setStatus('success');
      toast({
        title: 'Succès',
        description: 'Les traductions manquantes ont été ajoutées avec succès'
      });
      
      // Reload page to refresh translations
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Failed to add translations:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Échec de l\'ajout des traductions');
      toast({
        title: 'Erreur',
        description: 'Échec de l\'ajout des traductions',
        variant: 'destructive'
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Languages className="h-5 w-5" />
          Ajouter les traductions manquantes
        </CardTitle>
        <CardDescription>
          Ajoutez automatiquement toutes les traductions manquantes pour l'interface d'administration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === 'success' && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Les traductions ont été ajoutées avec succès ! La page va se recharger...
            </AlertDescription>
          </Alert>
        )}

        {status === 'error' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Erreur: {errorMessage}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <h3 className="font-semibold">Traductions qui seront ajoutées:</h3>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
            <li>En-têtes et navigation (Header)</li>
            <li>Tableau de bord administration</li>
            <li>Gestion des vendeurs</li>
            <li>Colonnes de tableaux</li>
            <li>Messages communs</li>
            <li>Et plus encore...</li>
          </ul>
        </div>

        <Button 
          onClick={handleAddTranslations} 
          disabled={isAdding || status === 'success'}
          className="w-full"
        >
          {isAdding ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ajout en cours...
            </>
          ) : status === 'success' ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Ajouté avec succès
            </>
          ) : (
            <>
              <Languages className="mr-2 h-4 w-4" />
              Ajouter les traductions
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground">
          Cette action ajoutera toutes les traductions manquantes en Français, Anglais, Lingala et Swahili.
        </p>
      </CardContent>
    </Card>
  );
};
